import { Component, inject, OnInit, OnDestroy } from '@angular/core';
import { IonTitle, MenuController, NavController } from '@ionic/angular/standalone';
import { Subscription, of } from 'rxjs';
import { switchMap, catchError } from 'rxjs/operators';
import { AuthService } from 'src/app/core/services/auth/auth.service';
import { Business } from 'src/app/core/models/Business';
import { User } from 'src/app/core/models/User';
import { BusinessService } from 'src/app/core/services/business/business.service';
import { BusinessManagementService } from 'src/app/core/services/business/business-management.service';
import { BusinessUser } from 'src/app/core/models/BusinessUser';
import {
  IonContent,
  IonList,
  IonItem,
  IonLabel,
  IonText,
  IonNote,
  IonIcon,
  IonImg,
  IonSkeletonText
} from '@ionic/angular/standalone';
import { AlertService } from 'src/app/core/services/navigation/alert.service';
import { TabService } from 'src/app/core/services/navigation/tab.service';
import { StateType } from 'src/app/core/models/StateType';
import { CommonModule } from '@angular/common';
import { BusinessAvatarComponent } from '../business-avatar/business-avatar.component';
import { addIcons } from 'ionicons';
import { addCircle } from 'ionicons/icons';

@Component({
  selector: 'app-business-menu',
  templateUrl: './menu-business.component.html',
  styleUrls: ['./menu-business.component.scss'],
  standalone: true,
  imports: [
    IonContent,
    IonList,
    IonItem,
    IonLabel,
    IonText,
    IonNote,
    IonIcon,
    IonTitle,
    IonImg,
    IonSkeletonText,
    CommonModule,
    BusinessAvatarComponent
  ],
})
export class MenuBusinessComponent implements OnInit, OnDestroy {
  private readonly subscriptions: Subscription = new Subscription();
  private menuController: MenuController = inject(MenuController);
  private businessManagementService: BusinessManagementService = inject(BusinessManagementService);

  selectedBusiness: Business | null = null;
  loggedInUser: User | null = null;
  currentBusinessUser: BusinessUser | null = null;
  _imageUrl: string = 'assets/ic-business.svg';
  businessName: string = 'My Business';
  canViewDonationHistory: boolean = false;

  constructor(
    private authService: AuthService,
    private businessService: BusinessService,
    private navController: NavController,
    private alertService: AlertService,
    private tabService: TabService,
  ) {
    addIcons({ addCircle });
  }

  ngOnInit(): void {
    this.getBusinessInfo();
    this.getUserInfo();
    this.getCurrentBusinessUser();
  }

  getBusinessInfo(): void {
    const businessSubscription: Subscription = this.businessService.business$.subscribe({
      next: (business: Business | null) => {
        if (business) {
          this.selectedBusiness = business;
          this.businessName = business.name || 'My Business';
          if (business.avatarUrl) {
            this._imageUrl = business.avatarUrl;
          }
        }
      }
    });
    this.subscriptions.add(businessSubscription);
  }

  getUserInfo(): void {
    const userSubscription: Subscription = this.authService.user$.subscribe({
      next: (user: User | null) => {
        this.loggedInUser = user;
      }
    });
    this.subscriptions.add(userSubscription);
  }

  getCurrentBusinessUser(): void {
    // Use switchMap to cancel previous HTTP request when business changes, preventing race conditions
    // Use catchError inside switchMap to handle HTTP errors without terminating the outer stream
    const businessUserSubscription: Subscription = this.businessService.business$.pipe(
      switchMap((business: Business | null) => {
        if (business?.id) {
          return this.businessManagementService.getCurrentBusinessUser(business.id.toString()).pipe(
            catchError(() => {
              // Return null on error to keep the outer subscription alive
              this.canViewDonationHistory = false;
              return of(null);
            })
          );
        }
        return of(null);
      })
    ).subscribe({
      next: (businessUser: BusinessUser | null) => {
        this.currentBusinessUser = businessUser;
        this.canViewDonationHistory = businessUser?.role === 'OWNER' || businessUser?.role === 'ADMIN';
      }
    });
    this.subscriptions.add(businessUserSubscription);
  }

  switchToUserPortal(): void {
    this.alertService
      .showAlertForDonation('Switch Portal', 'Do you want to switch to the user portal?')
      .subscribe((res: HTMLIonAlertElement) => {
        res.onDidDismiss().then((data) => {
          if (data.role === 'yes') {
            this.menuController.close('business-menu');
            const lastPage: { url: string; state: StateType } | null = this.tabService.getLastPage('user');
            this.navController.navigateRoot(lastPage?.url || '/main/home', {
              state: lastPage?.state || {},
            });
          }
        });
      });
  }

  updateImage(file: File): void {
    if (this.selectedBusiness?.id) {
      const id: number = this.selectedBusiness.id;
      const formData: FormData = new FormData();
      formData.append('image', file, file.name);
      
      const updateSubscription: Subscription = this.businessService
        .changeBusinessProfilePic(id, formData)
        .subscribe({
          next: () => {
            // Force refresh the business data to get the new avatarUrl
            this.businessService.setCurrentBusiness(id.toString(), false);
          },
          error: () => {
            this.alertService.showAlertMessage(
              'Profile picture was not uploaded',
              'There was an upload error, please try again later.',
            );
          }
        });
      this.subscriptions.add(updateSubscription);
    }
  }

  goToMyProfile(): void {
    if (!this.selectedBusiness?.id) {
      return;
    }
    this.menuController.close('business-menu');
    this.navController.navigateForward(['/business/business', this.selectedBusiness.id]);
  }

  goToSettings(): void {
    this.menuController.close('business-menu');
    this.navController.navigateForward(['/business/settings']);
  }

  goToTeamManagement(): void {
    this.menuController.close('business-menu');
    this.navController.navigateForward(['/business/team-management']);
  }

  goToShopifyManagement(): void {
    this.menuController.close('business-menu');
    this.navController.navigateForward(['/business/shopify-management']);
  }

  goToStripeManagement(): void {
    this.menuController.close('business-menu');
    this.navController.navigateForward(['/business/stripe-account']);
  }

  goToOrders(): void {
    this.menuController.close('business-menu');
    this.navController.navigateForward(['/business/orders']);
  }

  goToDonationHistory(): void {
    this.menuController.close('business-menu');
    this.navController.navigateForward(['/business/donation-history']);
  }

  async signOut(): Promise<void> {
    await this.authService.logout();
    this.menuController.close('business-menu');
    this.navController.navigateRoot('/welcome');
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
